import { useState, useRef, useEffect } from 'react';
import { apiFetch } from '../api';
import { API_BASE } from '../config';
import KPICard from '../components/KPICard';
import DataFreshness from '../components/DataFreshness';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import { useSwrStore, useSwrQuery } from '../swrCache';

// How long to wait after the last keystroke before the search term actually
// triggers a server request — typing "s", "so", "sou" previously fired 3
// separate full round trips (each one a genuine cache miss, since the SWR
// store keys on the exact search string). Found during the 2026-10-22
// search performance investigation: at current row counts this wasn't
// visibly slow, but it's 2 wasted requests out of 3 for every word typed.
const SEARCH_DEBOUNCE_MS = 300;

const PURPLE_600 = '#6E58A6';

// "Low stock" has no reorder-level column to key off (deliberately not
// carried over from the source sheet — see backend) — reusing the old
// dashboard's flat cutoff as a documented, easily-tunable placeholder.
const LOW_STOCK_THRESHOLD = 50;

function stockColor(qty) {
    if (qty <= 0) return '#991B1B';
    if (qty <= LOW_STOCK_THRESHOLD) return '#B45309';
    return '#065F46';
}
function stockBg(qty) {
    if (qty <= 0) return '#FEE2E2';
    if (qty <= LOW_STOCK_THRESHOLD) return '#FEF3C7';
    return '#D1FAE5';
}

export default function StockDashboard({ user }) {
    const [status, setStatus] = useState([]);
    const [category, setCategory] = useState([]);
    const [subCategory, setSubCategory] = useState([]);
    const [stockLevelFilter, setStockLevelFilter] = useState(null); // 'zero' | 'low' | 'incoming' | null
    const [search, setSearch] = useState('');       // what the input shows, updates every keystroke
    const [debouncedSearch, setDebouncedSearch] = useState(''); // what actually drives the fetch
    const [uploadMsg, setUploadMsg] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    const store = useSwrStore();

    // KPIs/filter options/freshness never depended on status/category/
    // search (always computed over the whole snapshot) — fetched once via
    // its own stable cache key instead of being re-bundled into every
    // search request.
    const { data: meta, refresh: refreshMeta } = useSwrQuery(store, 'meta', () => apiFetch('inventory-stock', { action: 'meta' }));

    const cacheKey = `status=${status.join(',')}&category=${category.join(',')}&sub_category=${subCategory.join(',')}&search=${debouncedSearch}`;
    const { data, loading, error: fetchError, fetchedAt, refresh } = useSwrQuery(store, cacheKey, () => {
        const params = {};
        if (status.length) params.status = status.join(',');
        if (category.length) params.category = category.join(',');
        if (subCategory.length) params.sub_category = subCategory.join(',');
        if (debouncedSearch) params.search = debouncedSearch;
        return apiFetch('inventory-stock', params);
    });
    const error = fetchError ? 'Could not load stock data — the database may be slow right now. Try again in a moment.' : '';
    const showLoadingOverlay = loading && !data;

    const stock = data?.stock || [];
    // Client-side only — the main GET has no row cap, so `stock` already
    // holds every row matching the current status/category/search filters.
    // Thresholds match inventory-stock.js's `meta` action exactly (Low
    // Stock is inclusive of zero-stock rows there too), so the number on
    // each KPI card matches the row count you get after clicking it.
    const filteredStock = stock.filter(r => {
        if (stockLevelFilter === 'zero') return (r.current_stock || 0) === 0;
        if (stockLevelFilter === 'low') return (r.current_stock || 0) <= LOW_STOCK_THRESHOLD;
        if (stockLevelFilter === 'incoming') return (r.upcoming_stock || 0) > 0;
        return true;
    });
    const kpis = meta?.kpis || {};
    const statusOptions = ['Active', 'Discontinued', 'Festive', 'PM', 'Free'].map(s => ({ value: s, label: s }));
    const categoryOptions = (meta?.filter_options?.categories || []).map(c => ({ value: c, label: c }));
    const subCategoryOptions = (meta?.filter_options?.sub_categories || []).map(c => ({ value: c, label: c }));

    async function handleUpload(file) {
        setUploading(true);
        setUploadMsg(null);
        try {
            const form = new FormData();
            form.append('file', file);
            const res = await fetch(`${API_BASE}/inventory-stock?action=upload&uploaded_by=${encodeURIComponent(user?.username || 'Portal')}`, { method: 'POST', body: form });
            const body = await res.json();
            if (res.ok && body.success) {
                setUploadMsg({ type: 'success', text: `${body.inserted} SKU rows uploaded` });
                store.clear();
                refreshMeta();
                refresh();
            } else {
                setUploadMsg({ type: 'error', text: body?.error || 'Upload failed' });
            }
        } catch (err) {
            setUploadMsg({ type: 'error', text: 'Network error' });
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="stock-dashboard-page">
            <style>{`
                .stock-dashboard-page .kpi-card { border-top-color: #E4DCF0; }
                .stock-dashboard-page .kpi-card .sub { color: #8C82A0; }
                .sd-loading-overlay { position: absolute; inset: 0; z-index: 10; display: flex; align-items: center; justify-content: center; }
            `}</style>

            <div style={{
                display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
                background: '#0B0A12', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 6px 20px rgba(35,28,48,0.10)', padding: '14px 20px', marginBottom: 18,
            }}>
                <div>
                    <h1 style={{ fontWeight: 700, fontSize: 22, margin: 0, color: '#F3EFFA', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>Stock Dashboard</h1>
                    {meta?.freshness && (
                        <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#A79CBD' }}>
                            File uploaded {new Date(meta.freshness.uploaded_at).toLocaleString('en-IN')}
                            {meta.freshness.uploaded_by ? ` by ${meta.freshness.uploaded_by}` : ''}
                        </p>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
                    <DataFreshness fetchedAt={fetchedAt} loading={loading} onRefresh={refresh} style={{ color: '#A79CBD' }} />
                    <input
                        ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                        onChange={e => e.target.files[0] && handleUpload(e.target.files[0])}
                    />
                    <button
                        type="button" disabled={uploading} onClick={() => fileRef.current?.click()}
                        style={{
                            border: 0, background: PURPLE_600, color: '#fff', borderRadius: 999,
                            padding: '7px 16px', fontSize: 12.5, fontWeight: 700, cursor: uploading ? 'default' : 'pointer',
                            opacity: uploading ? 0.6 : 1,
                        }}
                    >
                        {uploading ? 'Uploading…' : '⭱ Upload Stock File'}
                    </button>
                </div>
            </div>

            {uploadMsg && (
                <p style={{
                    margin: '0 0 14px', fontSize: 13, fontWeight: 600,
                    color: uploadMsg.type === 'success' ? '#0F7B4F' : '#B42318',
                }}>
                    {uploadMsg.text}
                </p>
            )}

            <div style={{ position: 'relative' }}>
                {showLoadingOverlay && (
                    <div className="sd-loading-overlay">
                        <p className="loading">Loading stock data...</p>
                    </div>
                )}
                <div style={showLoadingOverlay ? { filter: 'blur(4px)', pointerEvents: 'none' } : undefined}>
                    {error ? <p className="empty" style={{ color: '#EF4444' }}>{error}</p> : !data ? <p className="loading">Loading...</p> : (
                        <>
                            <div className="kpi-row">
                                <KPICard label="Total SKUs" value={(kpis.total_skus || 0).toLocaleString('en-IN')} sub="Tracked in this snapshot" />
                                <KPICard label="Current Stock" value={(kpis.total_current_stock || 0).toLocaleString('en-IN')} sub="Units on hand" />
                                <KPICard label="Zero Stock" value={(kpis.zero_stock_count || 0).toLocaleString('en-IN')} sub="SKUs out of stock"
                                    onClick={() => setStockLevelFilter(f => f === 'zero' ? null : 'zero')} active={stockLevelFilter === 'zero'} />
                                <KPICard label="Low Stock" value={(kpis.low_stock_count || 0).toLocaleString('en-IN')} sub={`≤ ${LOW_STOCK_THRESHOLD} units`}
                                    onClick={() => setStockLevelFilter(f => f === 'low' ? null : 'low')} active={stockLevelFilter === 'low'} />
                                <KPICard label="Stock Incoming" value={(kpis.incoming_stock_count || 0).toLocaleString('en-IN')} sub="SKUs with upcoming stock"
                                    onClick={() => setStockLevelFilter(f => f === 'incoming' ? null : 'incoming')} active={stockLevelFilter === 'incoming'} />
                            </div>

                            <div className="filter-bar" data-tutorial="stock-search">
                                <input
                                    type="text" placeholder="Search SKU name or EAN..." value={search}
                                    onChange={e => setSearch(e.target.value)} style={{ minWidth: 250 }}
                                />
                                <MultiSelectDropdown values={status} options={statusOptions} onChange={setStatus} placeholder="Status" />
                                {categoryOptions.length > 0 && (
                                    <MultiSelectDropdown values={category} options={categoryOptions} onChange={setCategory} placeholder="Category" />
                                )}
                                {subCategoryOptions.length > 0 && (
                                    <MultiSelectDropdown values={subCategory} options={subCategoryOptions} onChange={setSubCategory} placeholder="Sub-category" />
                                )}
                                <span style={{ fontSize: 13, color: '#94A3B8', marginLeft: 'auto' }}>{filteredStock.length} SKUs</span>
                            </div>

                            <div className="table-wrapper">
                                {filteredStock.length === 0 ? (
                                    <p style={{ padding: 40, textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>
                                        {stock.length === 0 ? 'No stock data yet. Upload an inventory file to get started.' : 'No SKUs match this filter.'}
                                    </p>
                                ) : (
                                    <table>
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left', paddingLeft: 16 }}>#</th>
                                                <th>EAN</th>
                                                <th>Status</th>
                                                <th style={{ textAlign: 'left' }}>Product Name</th>
                                                <th>Current Stock</th>
                                                <th>Upcoming Stock</th>
                                                <th>Expected</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredStock.map((r, i) => (
                                                <tr key={r.id}>
                                                    <td style={{ paddingLeft: 16, color: '#94A3B8' }}>{i + 1}</td>
                                                    <td style={{ fontSize: 12, color: '#64748B' }}>{r.ean_code || '—'}</td>
                                                    <td style={{ fontSize: 12 }}>{r.product_status || '—'}</td>
                                                    <td style={{ textAlign: 'left', fontWeight: 600, fontSize: 13 }}>{r.product_name}</td>
                                                    <td>
                                                        <span style={{
                                                            display: 'inline-block', padding: '4px 12px', borderRadius: 20,
                                                            background: stockBg(r.current_stock), color: stockColor(r.current_stock),
                                                            fontWeight: 700, fontSize: 13,
                                                        }}>
                                                            {(r.current_stock || 0).toLocaleString('en-IN')}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: 13, color: r.upcoming_stock > 0 ? '#1D4ED8' : '#94A3B8' }}>
                                                        {r.upcoming_stock > 0 ? r.upcoming_stock.toLocaleString('en-IN') : '—'}
                                                    </td>
                                                    <td style={{ fontSize: 12, color: '#64748B' }}>{r.upcoming_stock_note || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
